import { RichText } from 'prismic-reactjs';
import ColoredSlice from '@monofront/design-system/organisms/ColoredSlice';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { Tabs, TabPanel } from '@monofront/design-system/components/Tabs';
import { IPrismicImage, IPrismicRichText } from '@monofront/types';
import logger from '@utils/logger';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import CustomPrismicLink from '@components/CustomPrismicLink';
import { StickerWithImage } from './StickerWithImage';
import * as prismicH from '@prismicio/helpers';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapBubblesInTabs } from './mapBubblesInTabs';

interface IBubbleItem {
  tab?: 'tab1' | 'tab2' | null;
  keyDataIllustration?: IPrismicImage | null;
  mainInformation?: string | null;
  keyDataLabel?: string | null;
  keyDataLegend?: IPrismicRichText | null;
}

export interface IBubblesInTabs {
  primary?: {
    title?: IPrismicRichText | null;
    titleIcon?: IPrismicImage | null;
    tabTitle1?: string | null;
    tabTitle2?: string | null;
  };
  items: IBubbleItem[];
}

const BubblesInTabs: React.FC<IBubblesInTabs> = ({ items, primary }) => {
  if (!primary || !primary.title?.length || !items?.length) {
    logger.warn(
      'Please make sure the BubblesInTabs slice is correctly filled in Prismic.',
    );

    return null;
  }

  const tabTitle = {
    tab1: primary.tabTitle1 || '',
    tab2: primary.tabTitle2 || '',
  };

  const bubblesByPanel = items.reduce((panels, currentBubble) => {
    if (!currentBubble.tab) return panels;

    panels[currentBubble.tab] = panels[currentBubble.tab] || [];
    panels[currentBubble.tab].push(currentBubble);

    return panels;
  }, {} as Record<string, IBubbleItem[]>);

  return (
    <>
      <ColoredSlice
        UNSAFE_className="bubbles-in-tabs"
        title={
          <div className="title-block tw-flex tw-items-baseline tw-justify-center">
            <RichText
              render={primary.title}
              htmlSerializer={CustomPrismicHeading({
                className: 'catchy-title tw-mb-4',
              })}
            />
            {primary.titleIcon?.url && (
              <span className="title-icon">
                <ResponsiveImage
                  width={primary.titleIcon.dimensions?.width}
                  height={primary.titleIcon.dimensions?.height}
                  src={primary.titleIcon.url}
                  alt={primary.titleIcon.alt}
                />
              </span>
            )}
          </div>
        }
        content={
          <Tabs>
            {Object.values(bubblesByPanel).map((panel, index) => (
              <TabPanel key={index} label={tabTitle[panel[0].tab!]}>
                <div className="bubble-list tw-flex tw-flex-wrap tw-justify-around">
                  {panel.slice(0, 3).map((bubble, index) => {
                    if (
                      !bubble.mainInformation ||
                      !bubble.keyDataLabel ||
                      !bubble.keyDataIllustration?.url
                    )
                      return null;

                    const legendHasText = Boolean(
                      prismicH.asText(bubble.keyDataLegend),
                    );

                    return (
                      <StickerWithImage
                        key={index}
                        highlightedText={bubble.mainInformation}
                        caption={bubble.keyDataLabel}
                        image={bubble.keyDataIllustration}
                        legend={
                          legendHasText ? (
                            <RichText
                              render={bubble.keyDataLegend}
                              serializeHyperlink={CustomPrismicLink}
                            />
                          ) : undefined
                        }
                        orientation={index % 2 === 0 ? 'left' : 'right'}
                      />
                    );
                  })}
                </div>
              </TabPanel>
            ))}
          </Tabs>
        }
      />
      <style jsx>{`
        .title-icon {
          margin-right: ${primary?.titleIcon?.dimensions?.width
            ? `-${primary?.titleIcon?.dimensions?.width}px`
            : '0'};
        }
      `}</style>
    </>
  );
};

export default withSlice(BubblesInTabs, mapBubblesInTabs);
